import React from 'react';
import { Candidate } from '../../../../../../../../store/globalAskAica/types';
import { GLOBAL_ASK_AICA_COPY } from '../../../../../../../../store/globalAskAica/constants';
import CrossIcon from '../../../../../../../../assets/Svg/CrossIcon';
import styles from './CompanyPicker.module.scss';

interface CompanyPickerProps {
    /** ask_back.candidates — one row per candidate. Exactly one candidate renders the
     * same card as the "Did you mean X?" confirm-single case; two-or-more is the
     * disambiguation case. Same component either way. */
    candidates: Candidate[];
    /** Tap a candidate row — sends gate_reply:{selected_org_id}. */
    onSelect: (candidate: Candidate) => void;
    /** The card's "✕" — dismisses the picker without picking (see Figma 32804-43057). */
    onDismiss?: () => void;
    disabled?: boolean;
}

/** Renders an ask_back(kind:"pick") gate: radio-style candidate rows (avatar +
 * name LEFT, CIN RIGHT — `city` is always null on the wire and is never
 * rendered) separated by dividers (Figma 32804-43165). Handles both the
 * ≥2-candidate disambiguation case and the exactly-1-candidate confirm-single
 * case with the same markup. The raw ask_back.prompt (numbered-list text) is
 * deliberately NOT rendered here — the card title + rows already convey it
 * (the shared MessagesContainer never renders this turn's bubble either — the
 * PortfolioAskAicaSurfaceProvider filters the raw prompt out of messages).
 * No free-text row (RECUR-27369 redesign) — refinement now happens by typing a
 * normal message in the composer below, which the gate's /search resolves. */
const CompanyPicker: React.FC<CompanyPickerProps> = ({ candidates, onSelect, onDismiss, disabled = false }) => {
    return (
        <div className={styles.CompanyPicker} data-testid="company-picker">
            <div className={styles.Header}>
                <span className={styles.Title}>{GLOBAL_ASK_AICA_COPY.PICKER_TITLE}</span>
                {onDismiss && (
                    <button type="button" className={styles.DismissButton} onClick={onDismiss} aria-label="Dismiss">
                        <CrossIcon colour="#4d4d4d" width="12" height="12" />
                    </button>
                )}
            </div>

            <div className={styles.CandidateList}>
                {candidates.map((candidate) => (
                    <button
                        key={candidate.org_id}
                        type="button"
                        className={styles.CandidateRow}
                        onClick={() => onSelect(candidate)}
                        disabled={disabled}
                        data-testid="company-picker-row"
                    >
                        <span className={styles.RadioDot} aria-hidden="true" />
                        {/* Design (Figma 32804:43164) shows a real company logo here. The
                            ask_back candidate payload carries only org_id/name/cin/city — no
                            website — and getCompanyLogo() needs a hostname, so this keeps the
                            design's 24px square slot with the initial as a stand-in. Adding a
                            `website` (or `logo`) field to the candidate payload is all that is
                            needed to drop a real <img> in here. */}
                        <span className={styles.LogoSlot} aria-hidden="true" data-testid="company-picker-avatar">
                            {candidate.name.charAt(0).toUpperCase()}
                        </span>
                        <span className={styles.CandidateName}>{candidate.name}</span>
                        {candidate.cin && <span className={styles.CandidateCin}>{candidate.cin}</span>}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default CompanyPicker;
